import type { ReactNode } from "react";
import { Mail } from "lucide-react";
import type { WayId } from "@shared-core/domain/portal-sign-in/types";
import { Button } from "../primitives/button";
import { cn } from "../primitives/utils";
import { GoogleLogo, MicrosoftLogo } from "../shell/ProviderLogos";

function PasskeyIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      aria-hidden="true"
    >
      <circle cx="9" cy="8" r="4" />
      <path d="M3 20c0-3.3 2.7-6 6-6" />
      <circle cx="17" cy="15" r="2.4" />
      <path d="M17 17.4V22m0-2h2" />
    </svg>
  );
}

const ICONS: Record<WayId, () => ReactNode> = {
  microsoft: () => <MicrosoftLogo />,
  google: () => <GoogleLogo />,
  passkey: PasskeyIcon,
  code: () => <Mail />,
};

/** One way to sign in: its mark, its label, and "Last used here" at the right edge when this device used it last. */
export function WayButton({
  way,
  isLastUsed,
  children,
  type = "button",
  disabled,
  onClick,
}: {
  way: WayId;
  isLastUsed: boolean;
  children: ReactNode;
  type?: "button" | "submit";
  disabled?: boolean;
  onClick?: () => void;
}) {
  const Icon = ICONS[way];
  return (
    <Button
      type={type}
      variant="outline"
      className={cn(
        "h-10 w-full justify-start gap-2.5 px-3.5 font-medium",
        isLastUsed && "border-primary ring-1 ring-primary ring-inset",
      )}
      disabled={disabled}
      onClick={onClick}
    >
      <Icon />
      {children}
      {isLastUsed ? (
        <span className="ml-auto rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">
          Last used here
        </span>
      ) : null}
    </Button>
  );
}
